import type { Program } from '../../types/index.js'

/**
 * Tri et recherche des épisodes d'une série.
 *
 * Module séparé du composant : ces règles se testent sans monter React, et la
 * liste les partage avec tout écran qui aurait à retrouver un épisode.
 */

/** Numéro affiché, ou `null` si la série n'est pas numérotée. */
export function episodeNumber(episode: Program, numbered: boolean): string | null {
  if (!numbered) return null
  if (episode.season != null && episode.episode != null) {
    return `S${episode.season}E${String(episode.episode).padStart(2, '0')}`
  }
  return episode.episode != null ? `Ép. ${episode.episode}` : null
}

/**
 * Variantes de numéro acceptées par la recherche.
 *
 * L'écran affiche `S2E04`, mais l'usage courant écrit la saison sur deux
 * chiffres : chercher `S02E04` doit aboutir sur le même épisode. Les deux
 * formes sont donc comparées, sans changer ce qui est affiché.
 */
function searchableNumbers(episode: Program, numbered: boolean): string[] {
  const label = episodeNumber(episode, numbered)
  if (!label) return []
  if (episode.season == null || episode.episode == null) return [label]
  return [
    label,
    `S${String(episode.season).padStart(2, '0')}E${String(episode.episode).padStart(2, '0')}`,
  ]
}

/** Durée totale d'un épisode, parties et battements inclus. */
export function totalEpisodeDuration(episode: Program): number {
  return episode.totalDurationSec
    ?? episode.durationSec
      + (episode.parts ?? []).reduce((total, part) => total + part.durationSec, 0)
      + episode.interPartBreakSec * (episode.parts?.length ?? 0)
}

/**
 * Trie par saison puis épisode, et filtre sur ce que l'opérateur lit à
 * l'écran : titre, titre court publié et numéro.
 *
 * Chercher « S02E04 » doit aboutir même quand le titre ne reprend pas la
 * numérotation — sans ça, retrouver un épisode dans une série qui en porte cent
 * vingt obligerait à faire défiler toute la liste. À l'inverse, une série non
 * numérotée n'affiche aucun numéro : y chercher ne doit pas répondre sur une
 * valeur invisible.
 */
export function filterEpisodes(
  episodes: Program[],
  search: string,
  numbered: boolean,
): Program[] {
  // Le tri se fait sur une copie : la liste vient du parent, la réordonner en
  // place ferait sauter les lignes sous la souris.
  const sorted = [...episodes].sort((a, b) => {
    if ((a.season ?? 0) !== (b.season ?? 0)) return (a.season ?? 0) - (b.season ?? 0)
    if ((a.episode ?? 0) !== (b.episode ?? 0)) return (a.episode ?? 0) - (b.episode ?? 0)
    return a.title.localeCompare(b.title, 'fr')
  })
  const needle = search.trim().toLowerCase()
  if (!needle) return sorted
  return sorted.filter(episode => {
    return [episode.title, episode.titleShort ?? '', ...searchableNumbers(episode, numbered)]
      .some(field => field.toLowerCase().includes(needle))
  })
}
